import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, Inbox } from "lucide-react";

const STATUS = ["new", "contacted", "converted", "lost"];

export function AdminLeads() {
  const qc = useQueryClient();
  const { data: leads = [], isLoading } = useQuery({ queryKey: ["admin-leads"], queryFn: () => api.get("/leads").then((r) => r.data) });

  const setStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    qc.invalidateQueries({ queryKey: ["admin-leads"] });
    toast.success("Lead updated");
  };
  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-leads"] });
    toast.success("Lead deleted");
  };

  if (isLoading) return <p className="font-mono text-sm text-muted-foreground">Loading…</p>;
  if (!leads.length) return <Empty label="No leads captured yet." />;

  return (
    <div className="border border-border bg-card" data-testid="admin-leads">
      <div className="border-b border-border p-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">{leads.length} leads</div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["Name", "Contact", "Intent", "Budget", "Source", "Cost Sheet", "Status", ""].map((h) => (
                <TableHead key={h} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} className="border-border" data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-medium text-foreground">{l.name}</TableCell>
                <TableCell className="text-muted-foreground"><div>{l.email}</div><div className="font-mono text-xs">{l.phone}</div></TableCell>
                <TableCell className="text-muted-foreground">{l.intent}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{l.budget || "—"}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{l.source}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {l.cost_sheet?.totals?.grand
                    ? `₹${Math.round(l.cost_sheet.totals.grand).toLocaleString("en-IN")}`
                    : "—"}
                </TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                    <SelectTrigger className="h-8 w-32 rounded-none border-border" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Button size="icon" variant="ghost" onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function Empty({ label }) {
  return (
    <div className="flex flex-col items-center justify-center border border-border bg-card py-24 text-center">
      <Inbox className="h-8 w-8 text-muted-foreground" />
      <p className="mt-4 font-mono text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
